import { RepairOrderQuoteItems } from '../../form-state';
import { useRepairOrderQuotesStore } from '../repair-order-quotes-context';
import { NumberInput } from '../shared/number-input';
import { useAvailabilityStore } from './components/availability-store';
import { AvailabilityComponent } from './components/availability/availability-component';
import { BranchesComponent } from './components/branches/branches-component';
import { BrandCodeComponent } from './components/brand-code/brand-code-component';
import { PartNumberComponent } from './components/part-number/part-number-component';
import styles from './styles.module.scss';
import { RepairOrderQuoteStatusEnum } from 'api-new/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { FC, Fragment } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { formatCurrencySafe } from 'utils/formats';

type DescriptionComponentProps = {
  mainIndex: number;
  index: number;
};

const DescriptionComponent: FC<DescriptionComponentProps> = ({ mainIndex, index }) => {
  const { isEditMode } = useRepairOrderQuotesStore();

  const {
    register,
    watch,
    getValues,
    formState: { errors }
  } = useFormContext<RepairOrderQuoteItems>();
  const isDeleted = getValues(`items.${mainIndex}.parts.${index}.isDeleted`);
  return (
    <input
      tabIndex={-1}
      type="text"
      className={`form-control form-control-solid ${styles.partNumberInput} ${
        isDeleted || errors?.items?.[mainIndex]?.parts?.[index]?.description ? 'border-danger' : ''
      }`}
      {...register(`items.${mainIndex}.parts.${index}.description`)}
      disabled={isDeleted || !isEditMode || watch(`items.${mainIndex}.parts.${index}.isLookUped`)}
      autoFocus={false}
      style={{ ...(isDeleted ? { backgroundColor: '#fff5f8' } : {}) }}
    />
  );
};

type Props = {
  mainIndex: number;
  isShow: boolean;
};

export const PartsTable: FC<Props> = ({ mainIndex, isShow }) => {
  const { setAvailabilityState } = useAvailabilityStore();
  const { isEditMode, dataState } = useRepairOrderQuotesStore();

  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<RepairOrderQuoteItems>();
  const {
    fields,
    append,
    remove: removePart
  } = useFieldArray({
    control,
    name: `items.${mainIndex}.parts`,
    keyName: 'PartsItemId'
  });

  const partsValues = useWatch({ control, name: `items.${mainIndex}.parts` });

  const partPrice =
    partsValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const price = item.price ?? 0;
        const quantity = item?.quantity ?? 0;

        if (!quantity || !price) return sum;

        return sum + price * quantity;
      }, 0) ?? 0;

  const partDiscount =
    partsValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const discount = item?.discount;
        const price = item.price ?? 0;
        const quantity = item?.quantity ?? 0;

        if (!discount || !price || !quantity) return sum;

        return sum + (discount / 100) * price * quantity;
      }, 0) ?? 0;

  const partTotal =
    partsValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const price = item?.price ?? 0;
        const quantity = item?.quantity ?? 0;
        const discount = item?.discount ?? 0;
        const currentPrice = price * quantity;
        const currentDiscount = currentPrice * (discount / 100);
        const currentPriceInclVat = currentPrice - currentDiscount;

        return sum + currentPriceInclVat;
      }, 0) ?? 0;

  const handleChangeValue = (index: number, price: number, discount: number, quantity: number) => {
    const total = price * quantity;
    const totalVAT = total - (discount / 100) * total;

    setValue(`items.${mainIndex}.parts.${index}.totalExclVat`, Number((totalVAT / 1.2).toFixed(2)));
    setValue(`items.${mainIndex}.parts.${index}.totalInclVat`, Number(totalVAT.toFixed(2)));
  };

  const handleRemovePart = (rowIndex: number) => {
    const id = getValues(`items.${mainIndex}.parts.${rowIndex}.id`);

    if (!id) {
      removePart(rowIndex);
      return;
    }

    setValue(`items.${mainIndex}.parts.${rowIndex}.isDeleted`, true);
    setValue(`items.${mainIndex}.parts.${rowIndex}.needDelete`, true);
    setAvailabilityState();
  };

  const isDeleted = (rowIndex: number) => getValues(`items.${mainIndex}.parts.${rowIndex}.isDeleted`);

  const hasParts = Boolean(fields?.length);

  const getCdkIcon = (index: number) => {
    if (!Array.isArray(dataState) || mainIndex < 0 || mainIndex >= dataState.length) return null;

    const parts = dataState[mainIndex]?.parts;

    if (!Array.isArray(parts) || index < 0 || index >= parts.length) return null;

    const isSubmittedToCDK = parts[index]?.isSubmittedToCDK;

    if (isSubmittedToCDK == null) {
      return null;
    }

    const path = isSubmittedToCDK ? '/media/icons/custom/check-circle-2.svg' : '/media/icons/custom/information-3.svg';

    return <InlineSvg path={path} className="svg-icon svg-icon-1" />;
  };

  const handleAppendLine = () => {
    append({
      quantity: 1,
      brandCode: null,
      description: null,
      discount: 0,
      id: null,
      isLookUped: false,
      isManuallyFilled: true,
      listPriceExclVAT: null,
      listPriceInclVAT: null,
      partNumber: null,
      price: undefined,
      surchargeInclVAT: null,
      surchargeExclVAT: null,
      totalExclVat: null,
      totalInclVat: null
    });
  };

  const getAddPartBtn = (style?: string) => (
    <button type="button" onClick={handleAppendLine} className={clsx('btn btn-light-secondary text-nowrap', style)}>
      <InlineSvg path="/media/icons/custom/plus-circle.svg" className="svg-icon-2" />
      Add part manually
    </button>
  );
  return (
    <div className={styles.container}>
      <div className={styles.containerHeader}>Parts for this Job</div>
      {hasParts && (
        <div className="table-responsive" style={{ overflow: 'hidden' }}>
          <table className={clsx('table', styles.table)}>
            <thead>
              <tr>
                <th>Part Number</th>
                <th style={{ width: '20%' }}>Description</th>
                <th style={{ width: '50%' }}></th>
                <th style={{ width: '40%' }}></th>
                <th>Retail Price</th>
                <th>Quantity</th>
                <th>Discount, %</th>
                <th>Total + VAT</th>
                <th>CDK</th>
                {isEditMode && <th></th>}
              </tr>
            </thead>
            <tbody>
              {fields?.map((x, index) => (
                <Fragment key={x.PartsItemId}>
                  <tr>
                    <td className="d-md-none">
                      <span className={styles.headerItem}>Brand Code</span>
                      <div className="w-100">
                        <BrandCodeComponent mainIndex={mainIndex} rowIndex={index} />
                      </div>
                    </td>
                    <td>
                      <span className={styles.headerItem}>Part number</span>
                      <div className={styles.partNumberCell}>
                        <PartNumberComponent mainIndex={mainIndex} rowIndex={index} />
                      </div>
                    </td>
                    <td colSpan={3}>
                      <span className={styles.headerItem}>Description</span>
                      <DescriptionComponent mainIndex={mainIndex} index={index} />
                    </td>
                    <td className="min-w-md-90px">
                      <span className={styles.headerItem}>Retail Price</span>
                      <NumberInput
                        inputValue={watch(`items.${mainIndex}.parts.${index}.price`) ?? 0}
                        onBlur={(val) => {
                          const discount = getValues(`items.${mainIndex}.parts.${index}.discount`);
                          const quantity = getValues(`items.${mainIndex}.parts.${index}.quantity`);

                          handleChangeValue(index, val, discount ?? 0, quantity ?? 0);
                          setValue(`items.${mainIndex}.parts.${index}.price`, val);
                          trigger(`items.${mainIndex}.parts.${index}.price`);
                        }}
                        isDisabled={
                          isDeleted(index) || !isEditMode || watch(`items.${mainIndex}.parts.${index}.isLookUped`)
                        }
                        isError={isDeleted(index) || errors?.items?.[mainIndex]?.parts?.[index]?.price?.message != null}
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td className="min-w-md-70px">
                      <span className={styles.headerItem}>Quantity</span>
                      <NumberInput
                        inputValue={getValues(`items.${mainIndex}.parts.${index}.quantity`) ?? 0}
                        onBlur={(val) => {
                          const discount = getValues(`items.${mainIndex}.parts.${index}.discount`);
                          const price = getValues(`items.${mainIndex}.parts.${index}.price`);

                          handleChangeValue(index, price ?? 0, discount ?? 0, val ?? 0);
                          setValue(`items.${mainIndex}.parts.${index}.quantity`, val);
                          trigger(`items.${mainIndex}.parts.${index}.quantity`);
                        }}
                        isDisabled={isDeleted(index) || !isEditMode}
                        decimalPlaces={0}
                        isError={
                          isDeleted(index) || errors?.items?.[mainIndex]?.parts?.[index]?.quantity?.message != null
                        }
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td className="min-w-md-70px">
                      <span className={clsx(styles.headerItem, 'text-nowrap')}>Discount, %</span>
                      <NumberInput
                        inputValue={getValues(`items.${mainIndex}.parts.${index}.discount`) ?? 0}
                        onBlur={(val) => {
                          const price = getValues(`items.${mainIndex}.parts.${index}.price`);
                          const quantity = getValues(`items.${mainIndex}.parts.${index}.quantity`);

                          handleChangeValue(index, price ?? 0, val ?? 0, quantity ?? 0);

                          setValue(`items.${mainIndex}.parts.${index}.discount`, val);
                        }}
                        isDisabled={isDeleted(index) || !isEditMode}
                        decimalPlaces={0}
                        maxValue={100}
                        isError={isDeleted(index)}
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td className="min-w-md-100px">
                      <span className={styles.headerItem}>Total (incl VAT)</span>
                      <input
                        type="text"
                        className={clsx(
                          'form-control form-control-solid min-w-md-80px',
                          isDeleted(index) ? 'border-danger' : ''
                        )}
                        value={watch(`items.${mainIndex}.parts.${index}.totalInclVat`)?.toFixed(2) ?? 0}
                        onChange={() => console.log('asd')}
                        disabled={true}
                        style={{ ...(isDeleted(index) ? { backgroundColor: '#fff5f8' } : {}) }}
                      />
                    </td>
                    <td>
                      <span className={styles.headerItem} style={{ width: '105px' }}>
                        CDK
                      </span>
                      <div className="mt-md-2">{getCdkIcon(index)}</div>
                    </td>
                    {!isDeleted(index) &&
                      isEditMode &&
                      watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created && (
                        <td style={{ paddingTop: '15px' }}>
                          <button
                            onClick={() =>
                              confirm('Are you sure you want to delete this line?') && handleRemovePart(index)
                            }
                            className={styles.removeBtn}
                            type="button"
                          >
                            <InlineSvg path="/media/icons/custom/remove.svg" className="svg-icon-2" />
                          </button>
                        </td>
                      )}
                  </tr>
                  <tr className={styles.endBlockRow}>
                    <td>
                      <div style={{ maxWidth: '85px' }} className={clsx(styles.brandCodeDesktop, 'mt-1')}>
                        <BrandCodeComponent mainIndex={mainIndex} rowIndex={index} />
                      </div>
                    </td>
                    <td colSpan={7}>
                      <div className={styles.partInfoContainer}>
                        <div>
                          <BranchesComponent mainIndex={mainIndex} rowIndex={index} isShow={isShow} />
                        </div>

                        <div className={styles.availability}>
                          <AvailabilityComponent mainIndex={mainIndex} rowIndex={index} isShow={isShow} />
                        </div>

                        <div className={clsx(styles.priceSurchargeBlock, 'd-flex', 'gap-4', 'order-sm-1 order-md-0')}>
                          <div className={clsx(styles.listContainer, styles.listPrice)}>
                            <div>List Price</div>
                            <div className={styles.inputsContainer}>
                              <div className={clsx(styles.inputsRow)}>
                                <div>Price</div>
                                <div>
                                  <input
                                    type="text"
                                    className={clsx(
                                      'form-control form-control-solid',
                                      isDeleted(index) ? 'border-danger' : ''
                                    )}
                                    {...register(`items.${mainIndex}.parts.${index}.listPriceExclVAT`)}
                                    disabled={true}
                                    style={{ ...(isDeleted(index) ? { backgroundColor: '#fff5f8' } : {}) }}
                                  />
                                </div>
                              </div>

                              <div className={clsx(styles.inputsRow)}>
                                <div>Incl VAT</div>
                                <div>
                                  <NumberInput
                                    inputValue={watch(`items.${mainIndex}.parts.${index}.listPriceInclVAT`) ?? 0}
                                    onBlur={() => void 0}
                                    isDisabled={true}
                                    isError={isDeleted(index)}
                                    setBackColor={isDeleted(index)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={clsx(styles.priceSurchargeBlock, 'd-flex')}>
                            <div className={clsx(styles.listContainer)}>
                              <div>Surcharge</div>
                              <div className={styles.inputsContainer}>
                                <div className={clsx(styles.inputsRow)}>
                                  <div>Value, £</div>
                                  <div>
                                    <NumberInput
                                      inputValue={watch(`items.${mainIndex}.parts.${index}.surchargeExclVAT`) ?? 0}
                                      onBlur={(val) =>
                                        setValue(`items.${mainIndex}.parts.${index}.surchargeExclVAT`, val)
                                      }
                                      isDisabled={true}
                                      isError={isDeleted(index)}
                                      setBackColor={isDeleted(index)}
                                    />
                                  </div>
                                </div>

                                <div className={clsx(styles.inputsRow)}>
                                  <div>Incl VAT</div>
                                  <div>
                                    <NumberInput
                                      inputValue={watch(`items.${mainIndex}.parts.${index}.surchargeInclVAT`) ?? 0}
                                      onBlur={(val) =>
                                        setValue(`items.${mainIndex}.parts.${index}.surchargeInclVAT`, val)
                                      }
                                      isDisabled={true}
                                      isError={isDeleted(index)}
                                      setBackColor={isDeleted(index)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>

                    {isEditMode && <td></td>}
                  </tr>
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  {isEditMode &&
                    watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created &&
                    getAddPartBtn()}
                </td>
                <td colSpan={8} style={{ verticalAlign: 'top' }}>
                  <div className={clsx(styles.totalRow, 'd-grid gap-2  d-sm-flex')}>
                    <div className="d-flex gap-3">
                      <span className="w-sm-100 text-nowrap">Total Price</span>
                      <span className={clsx('w-sm-100', styles.totalRowBold)}>{formatCurrencySafe(partPrice)}</span>
                    </div>
                    <div className="d-flex gap-3">
                      <span className="w-sm-100 text-nowrap">Total Discount</span>
                      <span className={clsx('w-sm-100', styles.totalRowBold)}>{formatCurrencySafe(partDiscount)}</span>
                    </div>
                    <div className="d-flex  gap-3">
                      <span className="w-sm-100 text-nowrap">Total Selling Price (Incl VAT)</span>
                      <span className={clsx('w-sm-100', styles.totalRowBold)}>{formatCurrencySafe(partTotal)}</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!hasParts &&
        isEditMode &&
        watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created &&
        getAddPartBtn('mt-2')}
    </div>
  );
};
